import { launcherModelMetrics, launcherModelProperties, type LauncherAppearance } from './appearance';

/**
 * Construção do launcher a partir da aparência configurada no painel.
 *
 * Convive com o `buildLauncher` legado do `index.ts` em vez de substituí-lo: um
 * widget sem `appearance` — o estado de todos os que existem hoje — continua no
 * caminho antigo, e esta fatia não muda nada para os clientes atuais. É o
 * `normalizeAppearance` devolvendo `null` que faz essa separação.
 *
 * Difere do legado numa coisa estrutural: aqui a aparência chega com os valores
 * FINAIS, então não há mutação posterior. O legado constrói neutro e depois
 * pinta com `updateLauncherColors`/`applyLauncherTextColor`, que dependem de
 * nomes de classe — acoplamento que este caminho não herda.
 *
 * Todo texto entra por `textContent`, nunca `innerHTML`: os textos vêm da
 * configuração do cliente e são injetados no site dele. Interpretá-los como
 * HTML transformaria o painel — acessível a qualquer operador do tenant — num
 * vetor de execução no site em produção.
 */

const FONT = "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

interface LauncherBranding {
  primaryColor?: string | null;
  chatbotAvatarUrl?: string | null;
  chatbotName?: string | null;
}

export interface BuildLauncherOptions {
  appearance: LauncherAppearance;
  branding: LauncherBranding;
  position: 'left' | 'right';
  zIndex: number;
  onOpen?: () => void;
  onClose?: () => void;
  onQuickQuestion?: (question: string) => void;
}

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Pinta um elemento com a cor sólida ou o degradê.
 *
 * O `data-salevo-painted` existe para o teste alcançar o elemento pintado sem
 * depender da árvore, que difere entre os quatro modelos.
 */
function paint(el: HTMLElement, color: string, style: 'solid' | 'gradient'): void {
  el.setAttribute('data-salevo-painted', '');
  if (style === 'gradient') {
    el.style.backgroundImage = `linear-gradient(135deg, ${color} 0%, ${hexToRgba(color, 0.72)} 100%)`;
  } else {
    el.style.backgroundColor = color;
  }
}

/**
 * Anel de pulso.
 *
 * `box-shadow` animado, e não `transform`: o anel envolve o gatilho, e escalar
 * o wrapper mexeria no próprio botão. O keyframe é injetado uma vez por página
 * porque o loader não carrega folha de estilo — e o container do widget usa
 * `all: initial`, então nada do site do cliente vaza para dentro nem o
 * contrário.
 */
const PULSE_ANIMATION = 'salevo-launcher-pulse';

function ensurePulseKeyframes(): void {
  if (document.getElementById('salevo-launcher-pulse-style')) return;
  const style = document.createElement('style');
  style.id = 'salevo-launcher-pulse-style';
  style.textContent = `@keyframes ${PULSE_ANIMATION}{0%{box-shadow:0 0 0 0 var(--salevo-pulse-color)}70%{box-shadow:0 0 0 16px transparent}100%{box-shadow:0 0 0 0 transparent}}`;
  document.head.appendChild(style);
}

function wrapWithPulse(el: HTMLElement, color: string): HTMLElement {
  ensurePulseKeyframes();
  const ring = document.createElement('div');
  ring.setAttribute('data-salevo-pulse', '');
  Object.assign(ring.style, {
    display: 'inline-flex',
    borderRadius: '999px',
    animationName: PULSE_ANIMATION,
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.6, 1)',
  } satisfies Partial<CSSStyleDeclaration>);
  ring.style.setProperty('--salevo-pulse-color', hexToRgba(color, 0.4));
  ring.appendChild(el);
  return ring;
}

function buildAvatar(branding: LauncherBranding, size: string): HTMLElement {
  const holder = document.createElement('div');
  holder.setAttribute('data-salevo-avatar', '');
  Object.assign(holder.style, {
    width: size,
    height: size,
    borderRadius: '999px',
    overflow: 'hidden',
    flexShrink: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.22)',
    color: '#ffffff',
    fontWeight: '700',
    fontSize: '14px',
    fontFamily: FONT,
  } satisfies Partial<CSSStyleDeclaration>);

  if (branding.chatbotAvatarUrl) {
    const img = document.createElement('img');
    img.src = branding.chatbotAvatarUrl;
    img.alt = '';
    Object.assign(img.style, { width: '100%', height: '100%', objectFit: 'cover' });
    holder.appendChild(img);
  } else {
    holder.textContent = initialsFromName(branding.chatbotName ?? '');
  }

  return holder;
}

/** Ícone de conversa, em SVG inline — o loader não tem como carregar sprite. */
function buildChatIcon(): HTMLElement {
  const holder = document.createElement('div');
  holder.setAttribute('data-salevo-icon', '');
  Object.assign(holder.style, { display: 'flex', alignItems: 'center', justifyContent: 'center' });
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '26');
  svg.setAttribute('height', '26');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', '#ffffff');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z');
  svg.appendChild(path);
  holder.appendChild(svg);
  return holder;
}

function buildCloseButton(textColor: string, onClose?: () => void): HTMLButtonElement {
  const close = document.createElement('button');
  close.type = 'button';
  close.setAttribute('data-salevo-close', '');
  close.setAttribute('aria-label', 'Ocultar chat');
  Object.assign(close.style, {
    width: '28px',
    height: '28px',
    borderRadius: '999px',
    border: 'none',
    backgroundColor: 'rgba(255,255,255,0.22)',
    color: textColor,
    cursor: 'pointer',
    flexShrink: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    lineHeight: '1',
    padding: '0',
    fontFamily: FONT,
  } satisfies Partial<CSSStyleDeclaration>);
  close.textContent = '×';
  close.addEventListener('click', (event) => {
    // Sem isto o clique sobe para o gatilho e o chat ABRE ao tentar fechar.
    event.stopPropagation();
    event.preventDefault();
    onClose?.();
  });
  return close;
}

function buildTexts(appearance: LauncherAppearance): HTMLElement {
  const holder = document.createElement('div');
  Object.assign(holder.style, { minWidth: '0', textAlign: 'left' });

  if (appearance.mainText) {
    const main = document.createElement('div');
    main.setAttribute('data-salevo-main-text', '');
    Object.assign(main.style, {
      fontSize: '10px',
      fontWeight: '600',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      opacity: '0.9',
      color: appearance.textColor,
      fontFamily: FONT,
    } satisfies Partial<CSSStyleDeclaration>);
    main.textContent = appearance.mainText;
    holder.appendChild(main);
  }

  if (appearance.callToAction) {
    const cta = document.createElement('div');
    cta.setAttribute('data-salevo-cta', '');
    Object.assign(cta.style, {
      fontSize: '14px',
      fontWeight: '700',
      lineHeight: '1.2',
      color: appearance.textColor,
      fontFamily: FONT,
    } satisfies Partial<CSSStyleDeclaration>);
    cta.textContent = appearance.callToAction;
    holder.appendChild(cta);
  }

  return holder;
}

/**
 * A parte clicável do launcher, já pintada.
 *
 * Devolve a RAIZ VISUAL, que não é sempre o botão. Nos modelos com botão fechar
 * a raiz é uma `div` pintada contendo o gatilho e o fechar como IRMÃOS — botão
 * dentro de botão é HTML inválido, e o navegador desmonta a árvore, fazendo o
 * clique se comportar de formas difíceis de diagnosticar. Foi o que o teste
 * `não aninha botão dentro de botão` pegou.
 */
function buildTriggerRoot(
  appearance: LauncherAppearance,
  branding: LauncherBranding,
  color: string,
  onOpen?: () => void,
  onClose?: () => void
): HTMLElement {
  const props = launcherModelProperties(appearance.model);
  const metrics = launcherModelMetrics(appearance.model);
  const capsule = appearance.model === 'barra' || appearance.model === 'destacado';
  const comFechar = props.usesClose && appearance.showClose;

  /**
   * As medidas vão na RAIZ VISUAL, que muda com o botão de fechar: sem ele o
   * próprio gatilho é a raiz; com ele, a cápsula que envolve gatilho e fechar.
   * Aplicar no gatilho nos dois casos deixaria a cápsula do `destacado` mais
   * baixa que o pedido, porque ela cresceria só pelo conteúdo.
   *
   * O `data-salevo-capsule` marca essa raiz. Existe porque `data-salevo-painted`
   * não serve de âncora: no `destacado` o cabeçalho do painel de acesso rápido
   * também é pintado e vem antes no DOM, então um seletor por "pintado"
   * encontrava o painel em vez da cápsula.
   */
  const aplicarMedidas = (el: HTMLElement) => {
    el.setAttribute('data-salevo-capsule', '');
    Object.assign(el.style, {
      minWidth: `${metrics.minWidth}px`,
      minHeight: `${metrics.minHeight}px`,
    } satisfies Partial<CSSStyleDeclaration>);
  };

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.setAttribute('data-salevo-launcher-trigger', '');
  trigger.setAttribute('aria-label', 'Abrir chat');
  trigger.setAttribute('aria-expanded', 'false');
  Object.assign(trigger.style, {
    display: 'flex',
    alignItems: 'center',
    gap: capsule ? '10px' : '0',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    borderRadius: '999px',
    padding: '0',
    width: capsule ? 'auto' : '56px',
    height: capsule ? 'auto' : '56px',
    justifyContent: 'center',
    fontFamily: FONT,
  } satisfies Partial<CSSStyleDeclaration>);

  if (props.usesAvatar && appearance.showAvatar) {
    trigger.appendChild(buildAvatar(branding, `${metrics.avatarSize}px`));
  } else if (!props.usesAvatar) {
    trigger.appendChild(buildChatIcon());
  }
  if (props.usesText) trigger.appendChild(buildTexts(appearance));
  trigger.addEventListener('click', () => onOpen?.());

  if (!comFechar) {
    // Sem fechar, o próprio botão é a raiz visual: um elemento a menos.
    Object.assign(trigger.style, {
      overflow: 'hidden',
      padding: capsule ? '6px 16px 6px 8px' : '0',
      boxShadow: `0 4px 16px ${hexToRgba(color, 0.45)}`,
    } satisfies Partial<CSSStyleDeclaration>);
    // Nos redondos a largura fixa de 56px do `trigger` manda; a mínima só
    // acrescenta o piso das cápsulas, e a marca ancora o teste em todos.
    aplicarMedidas(trigger);
    paint(trigger, color, appearance.backgroundStyle);
    return trigger;
  }

  const capsuleRoot = document.createElement('div');
  Object.assign(capsuleRoot.style, {
    display: 'flex',
    alignItems: 'center',
    // `space-between` e não `flex-start`: com largura mínima maior que o
    // conteúdo, o botão de fechar tem que ir para a ponta da barra, senão sobra
    // um vão à direita e a cápsula parece cortada.
    justifyContent: 'space-between',
    gap: '8px',
    borderRadius: '999px',
    padding: '6px 10px 6px 8px',
    boxShadow: `0 4px 16px ${hexToRgba(color, 0.45)}`,
    fontFamily: FONT,
    boxSizing: 'border-box',
    cursor: 'pointer',
  } satisfies Partial<CSSStyleDeclaration>);
  aplicarMedidas(capsuleRoot);
  paint(capsuleRoot, color, appearance.backgroundStyle);

  /**
   * A cápsula INTEIRA abre o chat, não só o avatar.
   *
   * O gatilho é irmão do fechar porque botão dentro de botão é HTML inválido —
   * então ele ocupava só a largura do próprio conteúdo, e a largura mínima criou
   * uma faixa morta entre ele e o fechar. Clicar ali não fazia nada.
   *
   * São dois mecanismos, e cada um cobre o que o outro não alcança:
   *   `flex: 1` no gatilho  → a área de clique coincide com o BOTÃO, que é quem
   *                           recebe foco de teclado e mostra o cursor.
   *   listener na raiz      → cobre o padding da cápsula, que fica por fora do
   *                           gatilho por definição.
   *
   * O fechar não vira exceção nova: ele já chama `stopPropagation`, então o
   * clique nele não sobe até aqui.
   */
  Object.assign(trigger.style, {
    flex: '1',
    minWidth: '0',
    justifyContent: 'flex-start',
  } satisfies Partial<CSSStyleDeclaration>);
  capsuleRoot.addEventListener('click', (event) => {
    // Só quando o clique caiu na própria cápsula: vindo do gatilho, ele já
    // chamou `onOpen`, e reagir de novo abriria duas vezes.
    if (event.target === capsuleRoot) onOpen?.();
  });

  capsuleRoot.appendChild(trigger);
  capsuleRoot.appendChild(buildCloseButton(appearance.textColor, onClose));

  return capsuleRoot;
}

function buildQuickQuestionsPanel(
  appearance: LauncherAppearance,
  branding: LauncherBranding,
  color: string,
  onQuickQuestion?: (question: string) => void
): HTMLElement {
  const panel = document.createElement('div');
  panel.setAttribute('data-salevo-panel', '');
  Object.assign(panel.style, {
    width: `${launcherModelMetrics(appearance.model).panelWidth}px`,
    borderRadius: '14px',
    backgroundColor: '#ffffff',
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
    overflow: 'hidden',
    fontFamily: FONT,
  } satisfies Partial<CSSStyleDeclaration>);

  const header = document.createElement('div');
  Object.assign(header.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
  } satisfies Partial<CSSStyleDeclaration>);
  paint(header, color, appearance.windowBackgroundStyle);
  if (appearance.showAvatar) header.appendChild(buildAvatar(branding, '32px'));
  header.appendChild(buildTexts(appearance));
  panel.appendChild(header);

  const list = document.createElement('div');
  Object.assign(list.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '10px',
  } satisfies Partial<CSSStyleDeclaration>);

  for (const question of appearance.quickQuestions) {
    const item = document.createElement('button');
    item.type = 'button';
    item.setAttribute('data-salevo-question', '');
    Object.assign(item.style, {
      textAlign: 'left',
      fontSize: '12px',
      color: '#334155',
      backgroundColor: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '8px 10px',
      cursor: 'pointer',
      fontFamily: FONT,
    } satisfies Partial<CSSStyleDeclaration>);
    item.textContent = question;
    item.addEventListener('click', (event) => {
      event.stopPropagation();
      onQuickQuestion?.(question);
    });
    list.appendChild(item);
  }

  panel.appendChild(list);
  return panel;
}

export function buildAppearanceLauncher(options: BuildLauncherOptions): HTMLElement {
  const { appearance, branding, position, zIndex, onOpen, onClose, onQuickQuestion } = options;
  const color = appearance.buttonColor ?? branding.primaryColor ?? '#2563eb';

  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'fixed',
    bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
    display: 'flex',
    flexDirection: 'column',
    alignItems: position === 'left' ? 'flex-start' : 'flex-end',
    gap: '10px',
    zIndex: String(zIndex + 1),
    fontFamily: FONT,
  } satisfies Partial<CSSStyleDeclaration>);
  container.style[position] =
    position === 'left'
      ? 'calc(20px + env(safe-area-inset-left, 0px))'
      : 'calc(20px + env(safe-area-inset-right, 0px))';

  const props = launcherModelProperties(appearance.model);

  /**
   * O painel de destaque fica FORA do gatilho, como irmão.
   *
   * As perguntas são botões, e botão dentro de botão é HTML inválido: o
   * navegador desmonta a árvore e o clique passa a se comportar de formas
   * difíceis de diagnosticar. Por isso a raiz é uma `div` e não um `button`.
   */
  if (props.usesQuickQuestions && appearance.quickQuestions.length > 0) {
    container.appendChild(
      buildQuickQuestionsPanel(appearance, branding, color, onQuickQuestion)
    );
  }

  const triggerRoot = buildTriggerRoot(appearance, branding, color, onOpen, onClose);
  container.appendChild(appearance.pulse ? wrapWithPulse(triggerRoot, color) : triggerRoot);

  return container;
}
